import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Button, MessageBarBody, MessageBarTitle, Spinner } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

// component functions
import * as ComponentFunctions from './ConsumptionDetails.Functions';

// guía
import { GuideDrawer, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideKpiCard, GuideKpiGrid } from '../../../theme/components/GuideKpiCard';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { StatusPill } from '../../../theme/components/StatusPill';

// partes del detalle
import { ConsumptionDetailsValuesGrid } from './ConsumptionDetails.ValuesGrid';
import { ConsumptionDetailsClassification, ProducerCard } from './ConsumptionDetails.Classification';
import { ConsumptionDetailsConcept } from './ConsumptionDetails.Concept';
import { fmtCurrency, fmtCurrencyCode, settlementTotalsOf, statusToneOf, withholdingTotalsOf } from './ConsumptionDetails.Format';

// components
import ConsumptionDetailMenuComponent from './ConsumptionDetailsMenu/ConsumptionDetailMenu.Component';
import ConsumptionSettlementComponent from '../ConsumptionSettlement/Consumption.Settlement.Component';
import VisitConceptFormComponent from '../VisitConceptForm/VisitConcept.Form.Component';
import ChildConsumptionListComponent from '../ChildConsumptionList/ChildConsumption.List.Component';
import ConsumptionSourceProviderComponent from './ConsumptionSourceProvider/ConsumptionSourceProvider.Component';
import { hasSourceProviderConsumption } from '../../../functions/ProviderConsumptionImport.Functions';
import BilledInConceptListComponent from '../BilledInConceptList/BilledInConcept.List.Component';
import ConceptValueFormComponent from './ConceptValues/ConceptValue.Form.Component';
import ConceptValueDeleteComponent from './ConceptValues/ConceptValue.Delete.Component';
import { ProtocolConsumptionConceptValues } from '../../../models/Entities/Protocols/ProtocolConsumptionConceptValues';

// styles
import './ConsumptionDetails.Styles.css';

/**
 * Cuerpo del detalle: resumen (paciente, fecha, estado, origen, liquidación), total a liquidar
 * como tarjetas KPI, clasificación plegable, concepto de contrato, grilla de valores, costos y
 * listas relacionadas. Cada bloque es un componente propio: así los hooks quedan cortos y la
 * regla de hooks vuelve a poder analizarlos.
 */
const PanelInfo = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const c = props.Consumption!;

    // La clasificación arranca plegada: quien abre el detalle viene a ver importes y estado.
    const [isClassificationOpen, setIsClassificationOpen] = useState(false);

    // Cache del groupingId para que "Conceptos relacionados" no parpadee durante una actualización.
    const [cachedGroupingId, setCachedGroupingId] = useState<string | undefined>(undefined);
    useEffect(() => {
        if (c.groupingId) setCachedGroupingId(c.groupingId);
        else if (!props.isLoading && c.id) setCachedGroupingId(undefined);
    }, [c.groupingId, c.id, props.isLoading]);

    // ABM de conceptos adicionales: formulario y confirmación de baja.
    const [showConceptValueForm, setShowConceptValueForm] = useState(false);
    const [editingConceptValue, setEditingConceptValue] = useState<ProtocolConsumptionConceptValues | undefined>(undefined);
    const [deletingConceptValue, setDeletingConceptValue] = useState<ProtocolConsumptionConceptValues | undefined>(undefined);

    useEffect(() => { if (c.protocolId) props.GetAllProtocolContractAssociations(c.protocolId); }, [c.protocolId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (c.contractConceptId) props.GetContractConceptById(c.contractConceptId); }, [c.contractConceptId]); // eslint-disable-line react-hooks/exhaustive-deps
    // Contract.addendums se rehidrata primero: el reducer de conceptos por adenda no hace nada si la adenda no está.
    useEffect(() => { if (props.ContractId) props.GetContractById(props.ContractId); }, [props.ContractId]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { if (props.ContractId) props.GetContractAddendumsByContractId(props.ContractId); }, [props.ContractId, props.ContractLoaded]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (props.ContractId && props.ContractAddendumId !== undefined && props.AddendumLoaded) props.GetAllContractsConceptByAddendum(props.ContractId, props.ContractAddendumId);
    }, [props.ContractId, props.ContractAddendumId, props.AddendumLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    const isGrouped = Boolean(c.groupingId);
    const canEditConceptValues = Boolean(props.ContractId && c.id);
    const totals = settlementTotalsOf(c);
    const withholding = withholdingTotalsOf(c);
    const status = statusToneOf(c.status);
    const currency = fmtCurrencyCode(c.currencyCode);
    const hasBreakdown = Boolean(c.haveFeeDivision);

    return (
        <div className="cdt" data-testid="cdt-root">
            {/* ── Acciones sobre el consumo (ignorar / ignorar todos) ── */}
            {props.ProtocolConsumptionId && (
                <div className="cdt-toolbar" data-testid="cdt-toolbar">
                    <ConsumptionDetailMenuComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={props.ProtocolConsumptionId} />
                </div>
            )}

            {/* ── Resumen ── */}
            <div className="cdt-facts" data-testid="cdt-facts">
                <div className="cdt-fact">
                    <span className="cdt-fact__label">{t('consumptionDetails.patient')}</span>
                    <span className="cdt-fact__value">{c.patientNumberInProtocol || '—'}</span>
                </div>
                <div className="cdt-fact">
                    <span className="cdt-fact__label">{t('consumptionDetails.dueDate')}</span>
                    <span className="cdt-fact__value cdt-fact__value--num">{c.serviceDueDate ? moment(c.serviceDueDate).format('DD/MM/YYYY HH:mm') : '—'}</span>
                </div>
                <div className="cdt-fact">
                    <span className="cdt-fact__label">{t('consumptionDetails.status')}</span>
                    <span className="cdt-fact__value">
                        <StatusPill tone={status.tone} symbol={status.symbol}>{ComponentFunctions.GetStatusDescription(c.status)}</StatusPill>
                    </span>
                </div>
                {hasSourceProviderConsumption(c) && (
                    <ConsumptionSourceProviderComponent sourceProviderConsumptionId={c.sourceProviderConsumptionId!} providerIdentifier={c.providerId} providerName={c.providerName} />
                )}
                {c.settlementId && (
                    <div className="cdt-fact cdt-fact--wide">
                        <ConsumptionSettlementComponent ProtocolId={props.ProtocolId} ProtocolConsumptionId={c.id} />
                    </div>
                )}
            </div>

            {/* ── Total a liquidar ── */}
            <GuideKpiGrid className="cdt-kpis" data-testid="cdt-kpis">
                <GuideKpiCard label={t('consumptionDetails.totalToSettle')} value={fmtCurrency(totals.total, c.currencyCode)} tone="success" detail={t('consumptionDetails.amountsIn', { currency })} valueTestId="cdt-kpi-total" />
                {hasBreakdown && <GuideKpiCard label={t('consumptionDetails.center')} value={fmtCurrency(totals.ownerNet, c.currencyCode)} detail={t('consumptionDetails.divisionFeeNet')} />}
                {hasBreakdown && <GuideKpiCard label={t('consumptionDetails.medicalTeam')} value={fmtCurrency(totals.teamNet, c.currencyCode)} detail={t('consumptionDetails.divisionFeeNet')} />}
                {(withholding.total > 0 || (c.withHolingPercentage ?? 0) > 0) && (
                    <GuideKpiCard
                        label={t('consumptionDetails.totalWithholding')}
                        value={`−${fmtCurrency(withholding.total, c.currencyCode)}`}
                        tone="warning"
                        detail={withholding.additional > 0 ? t('consumptionDetails.withholdingDetail', { main: fmtCurrency(withholding.main, c.currencyCode), additional: fmtCurrency(withholding.additional, c.currencyCode) }) : undefined}
                    />
                )}
            </GuideKpiGrid>

            {/* ── Clasificación (plegable) ── */}
            {!isGrouped ? (
                <GuideDrawerSection
                    title={t('consumptionDetails.classificationSection')}
                    description={isClassificationOpen ? undefined : t('consumptionDetails.classificationHint')}
                    actions={
                        <Button appearance="subtle" size="small" icon={isClassificationOpen ? <ChevronDown16Regular /> : <ChevronRight16Regular />} aria-expanded={isClassificationOpen} aria-controls="cdt-classification" onClick={() => setIsClassificationOpen((open) => !open)} data-testid="cdt-classification-toggle">
                            {isClassificationOpen ? t('consumptionDetails.hide') : t('consumptionDetails.show')}
                        </Button>
                    }
                    testId="cdt-section-classification"
                >
                    <div id="cdt-classification" hidden={!isClassificationOpen}>
                        {isClassificationOpen && (
                            <ConsumptionDetailsClassification
                                consumption={c}
                                protocolId={props.ProtocolId}
                                contractId={props.ContractId}
                                isGrouped={isGrouped}
                                GetPersonByIdentifier={props.GetPersonByIdentifier}
                                ChangeProcedure={props.ChangeProcedure}
                                ChangeAdditional={props.ChangeAdditional}
                                SetConsumptionCondition={props.SetConsumptionCondition}
                                SetConsumptionGrouping={props.SetConsumptionGrouping}
                                ChangeVisit={props.ChangeVisit}
                                ChangeMedicalService={props.ChangeMedicalService}
                                ChangeProvider={props.ChangeProvider}
                                ChangeProducer={props.ChangeProducer}
                                isChangingMedicalService={props.isChangingMedicalService}
                                failChangingMedicalService={props.failChangingMedicalService}
                                errorChangingMedicalService={props.errorChangingMedicalService}
                                isChangingProvider={props.isChangingProvider}
                                failChangingProvider={props.failChangingProvider}
                                errorChangingProvider={props.errorChangingProvider}
                                isChangingProducer={props.isChangingProducer}
                                failChangingProducer={props.failChangingProducer}
                                errorChangingProducer={props.errorChangingProducer}
                            />
                        )}
                    </div>
                </GuideDrawerSection>
            ) : (
                /* Los consumos agrupados no tienen clasificación; el profesional sigue visible. */
                <GuideDrawerSection title={t('consumptionDetails.producer')} testId="cdt-section-producer">
                    <ProducerCard
                        consumption={c}
                        protocolId={props.ProtocolId}
                        isGrouped={isGrouped}
                        GetPersonByIdentifier={props.GetPersonByIdentifier}
                        ChangeProducer={props.ChangeProducer}
                        isChangingProducer={props.isChangingProducer}
                        failChangingProducer={props.failChangingProducer}
                        errorChangingProducer={props.errorChangingProducer}
                    />
                </GuideDrawerSection>
            )}

            {/* ── Concepto de contrato ── */}
            {!isGrouped && (
                <GuideDrawerSection title={t('consumptionDetails.conceptAssociated')} testId="cdt-section-concept">
                    <ConsumptionDetailsConcept
                        consumption={c}
                        protocolId={props.ProtocolId}
                        contractId={props.ContractId}
                        contractAddendumId={props.ContractAddendumId}
                        currentConcepts={props.CurrentConcepts}
                        currentStages={props.CurrentStages}
                        isChangingConcept={props.isChangingConcept}
                        failChangingConcept={props.failChangingConcept}
                        errorChangingConcept={props.errorChangingConcept}
                        ChangeContractConcept={props.ChangeContractConcept}
                    />
                </GuideDrawerSection>
            )}

            {/* ── Detalle de valores ── */}
            <GuideDrawerSection title={t('consumptionDetails.valuesDetail')} description={t('consumptionDetails.amountsIn', { currency })} testId="cdt-section-values">
                <ConsumptionDetailsValuesGrid
                    consumption={c}
                    protocolId={props.ProtocolId}
                    canEditConceptValues={canEditConceptValues}
                    onAddConceptValue={() => { setEditingConceptValue(undefined); setShowConceptValueForm(true); }}
                    onEditConceptValue={(value) => { setEditingConceptValue(value); setShowConceptValueForm(true); }}
                    onDeleteConceptValue={setDeletingConceptValue}
                />
            </GuideDrawerSection>

            {/* ── Costos ── */}
            {c.costAmount ? (
                <GuideDrawerSection title={t('consumptionDetails.costs')} testId="cdt-section-costs">
                    <dl className="cdt-costs">
                        <div className="cdt-costs__row"><dt>{t('consumptionDetails.calculatedCost')}</dt><dd>{fmtCurrency(c.costAmount, c.costCurrencyCode ?? 'ARS')}</dd></div>
                        {c.costPayee ? <div className="cdt-costs__row"><dt>{t('consumptionDetails.paid')}</dt><dd>{fmtCurrency(c.costPayee, c.costCurrencyCode ?? 'ARS')}</dd></div> : null}
                        {c.costPending ? <div className="cdt-costs__row cdt-costs__row--pending"><dt>{t('consumptionDetails.pending')}</dt><dd>{fmtCurrency(c.costPending, c.costCurrencyCode ?? 'ARS')}</dd></div> : null}
                    </dl>
                </GuideDrawerSection>
            ) : null}

            {/* ── Conceptos relacionados (agrupados) ── */}
            {cachedGroupingId !== undefined && cachedGroupingId !== null && (
                <GuideDrawerSection title={t('consumptionDetails.relatedConcepts')} description={t('consumptionDetails.relatedConceptsHint')} testId="cdt-section-related">
                    <ChildConsumptionListComponent ProtocolId={props.ProtocolId} CosnumptionId={c.id} />
                </GuideDrawerSection>
            )}

            {/* ── Conceptos incluidos dentro del concepto de contrato ── */}
            {c.id && (
                <GuideDrawerSection title={t('consumptionDetails.billedInConcepts')} description={t('consumptionDetails.billedInConceptsHint')} testId="cdt-section-billed-in">
                    <BilledInConceptListComponent ProtocolId={props.ProtocolId} CosnumptionId={c.id} />
                </GuideDrawerSection>
            )}

            {/* ABM de conceptos adicionales: los diálogos van al final para no quedar dentro de la grilla. */}
            {canEditConceptValues && c.id && (
                <>
                    <ConceptValueFormComponent
                        ProtocolId={props.ProtocolId}
                        ProtocolConsumptionId={c.id}
                        ContractId={props.ContractId}
                        ConceptValue={editingConceptValue}
                        isOpen={showConceptValueForm}
                        onClose={() => { setShowConceptValueForm(false); setEditingConceptValue(undefined); }}
                    />
                    <ConceptValueDeleteComponent
                        ProtocolId={props.ProtocolId}
                        ProtocolConsumptionId={c.id}
                        ConceptValue={deletingConceptValue}
                        isOpen={deletingConceptValue !== undefined}
                        onClose={() => setDeletingConceptValue(undefined)}
                    />
                </>
            )}
        </div>
    );
};

/**
 * Drawer del detalle de un consumo de protocolo (guía, ancho). El encabezado lleva la visita
 * como contexto, el servicio como título, el paciente y el estado como subtítulo, y las acciones
 * (ignorar, ver visita) a la derecha.
 */
const ConsumptionDetailsCompoment = (props: ComponentFunctions.Props) => {
    const [t] = useTranslation();
    const [showVisitConceptForm, setShowVisitConceptForm] = useState(false);

    // Abierto desde pantallas que no cargan el protocolo (p. ej. consumos de proveedor) el consumo traído
    // se descartaba y el drawer quedaba en blanco: primero se carga el protocolo y después el consumo.
    useEffect(() => {
        if (props.isOpen && props.ProtocolId && !props.ProtocolLoaded) props.GetMedicalProtocolById(props.ProtocolId);
    }, [props.isOpen, props.ProtocolId, props.ProtocolLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.isOpen && props.ProtocolLoaded && props.ProtocolId && props.ProtocolConsumptionId) props.GetProtocolConsumptionById(props.ProtocolId, props.ProtocolConsumptionId);
    }, [props.isOpen, props.ProtocolConsumptionId, props.ProtocolLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

    const canViewVisit = props.Consumption?.emrCrossVersionId !== undefined && Number(props.Consumption?.parentInstanceId) !== 0;
    const subtitle = props.Consumption
        ? [props.Consumption.patientNumberInProtocol ? `${t('consumptionDetails.patient')} ${props.Consumption.patientNumberInProtocol}` : undefined, props.Consumption.conceptDescription].filter(Boolean).join(' · ')
        : undefined;

    return (
        <div>
            <GuideDrawer
                open={props.isOpen}
                size="wide"
                eyebrow={props.Consumption?.stageName}
                title={<span className="cdt-title">{props.Consumption?.serviceName}</span>}
                subtitle={subtitle || undefined}
                onDismiss={props.onClose}
                closeLabel={t('common.close')}
                headerActions={props.Consumption && canViewVisit ? (
                    <Button appearance="subtle" onClick={() => setShowVisitConceptForm(true)} data-testid="cdt-view-visit">
                        {t('consumptionDetails.viewVisit')}
                    </Button>
                ) : undefined}
                secondaryAction={{ label: t('common.close') }}
                className="cdt-drawer"
                bodyClassName="cdt-body"
                testId="consumption-details-drawer"
            >
                {props.Consumption
                    ? <PanelInfo {...props} />
                    : (props.failLoading || props.failLoadingProtocol)
                        ? <GuideMessageBar intent="error" layout="multiline">
                            <MessageBarBody>
                                <MessageBarTitle>{t('common.error')}</MessageBarTitle>
                                {props.error?.ErrorMessage ?? t('consumptionDetails.loadError')}
                            </MessageBarBody>
                        </GuideMessageBar>
                        : <Spinner label={t('consumptionDetails.loading')} />}
            </GuideDrawer>
            {showVisitConceptForm && props.Consumption?.patientNumberInProtocol && props.ContractId && (
                <VisitConceptFormComponent
                    ContractId={props.ContractId}
                    isOpen={showVisitConceptForm}
                    onClose={() => setShowVisitConceptForm(false)}
                    protocolId={props.ProtocolId}
                    visitId={Number(props.Consumption?.parentInstanceId)}
                    patientId={props.Consumption?.patientNumberInProtocol}
                />
            )}
        </div>
    );
};

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(ConsumptionDetailsCompoment as any);
